import "../../kendo.dropdownbutton.js"; // Still required until kendo.ui is modularized
import { IKendoJQueryStatic, IKendoJQuery } from "../../core/index.js";
import { Widget } from "../../core/base/widget.js";
import { ICONS, STYLES, DOT } from "../constants.js";

declare const kendo: any;
declare const $: IKendoJQueryStatic;

/**
 * FileMenu provides dropdown button functionality for file attachments.
 * Handles actions like download, preview, and delete for individual files.
 *
 * This is an internal collaborator that extends Widget for event handling.
 */
export class FileMenu extends Widget {
    static options = {
        name: "ChatFileMenu",
        items: [] as any[]
    };

    private dropdownButton: any;

    /**
     * Constructs a new FileMenu instance.
     */
    constructor(element: IKendoJQuery, options: any) {
        super(element, $.extend(true, {}, FileMenu.options, options));

        this.setCommandAttributes();
        this.createDropdownButton();
    }

    /**
     * Creates a dropdown button on the element.
     */
    private createDropdownButton(): void {
        this.dropdownButton = new kendo.ui.DropDownButton(this.element, {
            items: (this.options as any).items,
            fillMode: "flat",
            icon: ICONS.attachmentMenu
        });

        this.dropdownButton
            .bind("click", this.onClick.bind(this))
            .bind("open", this.onOpen.bind(this))
            .bind("close", this.onClose.bind(this));
    }

    /**
     * Sets command attributes on menu items for identification.
     */
    private setCommandAttributes(): void {
        (this.options as any).items.forEach((item: any) => {
            item.attributes = item.attributes || {};
            item.attributes["data-command"] = item.name.toLowerCase();
            item.id = item.name.toLowerCase();
        });
    }

    /**
     * Handles dropdown button item click.
     */
    private onClick(e: any): void {
        const command = e.id;
        const file = $(e.sender.element).closest(DOT + STYLES.file);
        const message = $(e.sender.element).closest(DOT + STYLES.message);

        if (command) {
            this.executeCommand(command, $(e.sender.element), file, message);
        }
    }

    /**
     * Handles dropdown open event.
     */
    private onOpen(e: any): void {
        let setActive = true;
        const target = $(e.sender.element);

        if (target.closest(DOT + STYLES.messageRemoved).length || target.find(DOT + STYLES.typingIndicator).length) {
            e.preventDefault();
            setActive = false;
        }

        this.setActive(target, setActive);
    }

    /**
     * Handles dropdown close event.
     */
    private onClose(e: any): void {
        const target = $(e.sender.element);
        const message = target.closest(DOT + STYLES.message);
        this.setActive(target, false);
        this.trigger("close", { message });
    }

    /**
     * Executes a command from the file menu.
     */
    private executeCommand(command: string, item: JQuery, file: JQuery, message: JQuery): void {
        this.trigger("execute", { type: command, item, file, message });
    }

    /**
     * Sets the active state for a target element.
     */
    private setActive(target: JQuery, active: boolean): void {
        const bubble = target.closest(DOT + STYLES.bubble);

        if (active) {
            bubble.addClass(STYLES.active);
        } else {
            bubble.removeClass(STYLES.active);
        }
    }

    destroy(): void {
        if (this.dropdownButton) {
            this.dropdownButton.destroy();
            this.dropdownButton = null;
        }

        super.destroy();
    }
}
